import PrintServiceElectron from './PrintServiceElectron';
import PrintServiceAndroid from './PrintServiceAndroid';
import PrintServiceIOS from './PrintServiceIOS';
import PrintServiceWeb from './PrintServiceWeb';
import PrintableResult from './PrintableResult';
import { ownReceiptLogo, withReceiptLogo, withoutReceiptLogo } from './receiptLogo';
import Store from '../wrappers/Store';
import Device from '../utilities/Device';
import FiscalServiceLegacy from '../fiscal/FiscalServiceLegacy';
import { appStorage } from '../utilities/AppStorage';
import { localNetworkService } from '../services/LocalNetworkService';
import { AppEvent, Terminal } from '../models';
import { AppStorageKey, PrintRoleType } from '../types';
import printJS from 'print-js';
// Remote PRINT is LAN-first (.preferLocalNetwork(PRINT_LAN_WINDOW)): a single target, fired
// frequently, so it tries LAN and escalates to WAN only if the target doesn't reply within
// PRINT_LAN_WINDOW (or LAN can't reach) — a same-LAN print stays on LAN with no per-slip WAN
// fan-out; a cross-network target still gets it over WAN.
// Remote DISCOVERY (detectPrinters) is dual-transport (LAN + WAN): it must find EVERY
// reachable terminal, including WAN-only ones, so completeness wins over suppressing the
// (bounded, rare) WAN fan-out. It is dup-free — the query carries one hash, so a peer that
// receives it over both transports dedups it and replies once.
// The *_TIMEOUT values are overall deadlines (a WAN round-trip is two queue hops each way).
// Tuned against the cross-network smoke test (docs/RemoteTerminalWAN.md phase 4): a
// cellular WAN round-trip was measured at 3.6–5.2s (queued broadcast + Soketi push to the
// mobile terminal), so the prior 3s/6s deadlines expired before replies arrived.
const REMOTE_PRINT_TIMEOUT = 10000;
const PRINT_LAN_WINDOW = 1500;
const DETECT_PRINTERS_TIMEOUT = 10000;
class PrintService {
    device;
    terminal;
    printConfig = undefined;
    service = PrintServiceWeb;
    detectedPrinters = { local: [], remote: [] };
    logoCapability = undefined;
    constructor() {
    }
    /**
     * Initialize Printing Config
     */
    async init() {
        // Initialize the configuration
        await this.initConfiguration();
        // Initialize the printing service
        await this.initService();
        // Detect printers
        await this.detectPrinters();
    }
    /**
     * Initialize the configuration
     */
    async initConfiguration() {
        this.printConfig = await this.getConfig();
        // If no config is present, save the default one
        if (!this.printConfig) {
            this.printConfig = {
                [PrintRoleType.receipt]: undefined,
                [PrintRoleType.order]: undefined,
                [PrintRoleType.bar]: undefined,
                [PrintRoleType.kitchen]: undefined,
                [PrintRoleType.sector_1]: undefined,
                [PrintRoleType.sector_2]: undefined
            };
            this.configure(this.printConfig);
        }
    }
    /**
     * Initialize the appropriate printing service
     */
    async initService() {
        this.device = Device.get();
        this.terminal = Store.get('terminal');
        if (this.device?.platform === 'electron') {
            this.service = PrintServiceElectron;
        }
        else if (this.device?.platform === 'android') {
            this.service = PrintServiceAndroid;
        }
        else if (this.device?.platform === 'ios') {
            this.service = PrintServiceIOS;
        }
        else {
            this.service = PrintServiceWeb;
        }
        this.logoCapability = undefined;
        return this.service.init();
    }
    /**
     * Configure Printing
     */
    configure(config) {
        this.printConfig = config;
        appStorage.local().setItem(AppStorageKey.printConfig, config);
        appStorage.native().setItem(AppStorageKey.printConfig, config);
    }
    /**
     * Get Printing configuration
     */
    async getConfig() {
        let printConfig = await appStorage.local().getItem(AppStorageKey.printConfig);
        if (!printConfig) {
            printConfig = await appStorage.native().getItem(AppStorageKey.printConfig);
        }
        return printConfig || undefined;
    }
    /**
     * Detect Printers both on the local device as well as in local network if needed.
     * `onUpdate` receives the printers found so far — the local ones first, then again after
     * each remote terminal's answer — and whether every other known terminal has answered,
     * so a screen can list printers as they arrive and stop showing a search once nobody
     * is left to wait for.
     */
    async detectPrinters(localOnly = false, onUpdate) {
        this.detectedPrinters.local = await this.service.detectPrinters();
        if (localOnly)
            return { local: this.detectedPrinters.local, remote: [] };
        // Discovery must reach EVERY other terminal (a printer we want to assign may be on
        // one only reachable over WAN), so a broadcast can't safely suppress WAN on partial
        // LAN replies. Gated on the venue terminal roster (`Terminal.all()` — the network-
        // independent roster loaded at login):
        //  • solo venue (<=1 terminal): nothing to discover — LAN-only, returns instantly;
        //  • multi-terminal: dual-transport (LAN + WAN) for complete discovery. Dup-free —
        //    the query's single hash dedups at each receiver, so a peer reached over both
        //    transports replies once. (Storm-free with the backend relay preserving
        //    request_hash; detection is rare, so the bounded WAN fan-out is acceptable.)
        // Every terminal that receives the query answers it (AppEvent.onDetectPrinters), so
        // once every other known terminal has answered there is nobody left to wait for.
        // Known = the roster plus the peers connected on the LAN right now. The broadcast
        // itself still runs to its timeout: a terminal registered after this one loaded its
        // roster (and so unknown here) can answer late, and its printers still arrive.
        // An answer without a terminal (e.g. an open Admin tab) is neither expected nor
        // useful — its printers cannot be matched to a terminal — so it is not counted.
        const roster = Terminal.all();
        const ownKey = Store.get('terminal')?.key;
        const lanPeers = localNetworkService.connectionStatus?.terminals.filter(t => t.connected) ?? [];
        const expected = [...new Set([...roster.map(t => t.key), ...lanPeers.map(t => t.terminal?.key)])]
            .filter((key) => !!key && key !== ownKey);
        const answered = new Set();
        const local = this.detectedPrinters.local;
        const remote = [];
        const report = () => onUpdate?.({ local, remote: [...remote] }, expected.every(key => answered.has(key)));
        report();
        const event = AppEvent.build('detectPrinters').withResponse()
            .onEachResponse(response => {
            if (response.success && response.data?.local)
                remote.push(...response.data.local);
            if (response.terminal?.key)
                answered.add(response.terminal.key);
            report();
        });
        if (roster.length <= 1)
            event.overLocalNetwork();
        const sendEventResponse = await event.send(undefined, DETECT_PRINTERS_TIMEOUT);
        this.detectedPrinters.remote = sendEventResponse.filter(r => r.success && !!r.data)
            .flatMap(r => r.data?.local);
        return this.detectedPrinters;
    }
    /**
     * Print custom text / html to printer
     */
    async print(printable, options = {}) {
        let { printer, choosePrinter = false, wrapperClass, openDrawer = false } = options;
        if (!printer) {
            printer = this.getAssignedPrinter(PrintRoleType.receipt);
        }
        let printableText = await printable.toText({ charsPerLine: printer?.options?.charsPerLine || 32 });
        let printableHtml = this.shouldGenerateHtml(printable, printer)
            ? await printable.toHtml({ wrapperClass, forceLightTheme: true })
            : new PrintableResult();
        this.fillPrintContainer(printableHtml.forPrint() || `<pre>${printableText.forPrint()}</pre>`);
        if (choosePrinter || !printer) {
            const isMobile = this.device?.platform === 'android' || this.device?.platform === 'ios';
            if (isMobile) {
                return Promise.resolve({ success: false, error: 'receipt_printer_not_set_up' });
            }
            this.printHtml('print-container');
            return Promise.resolve({ success: true });
        }
        return this.sendToPrinter(printer, {
            text: printableText.forPrint(),
            html: printableHtml.forPrint(),
            highlight: printableText.getHighlight(),
            openDrawer
        });
    }
    /**
     * Open printer cash drawer
     */
    async openDrawer(localOnly = false) {
        const printer = this.getAssignedPrinter(PrintRoleType.receipt);
        if (!printer)
            return { success: false };
        const isLocal = this.terminal?.key === printer?.terminal?.key;
        if (localOnly || isLocal) {
            return this.service.openDrawer(printer);
        }
        if (!printer.terminal?.key)
            return { success: false };
        AppEvent.build('openDrawer').overLocalNetwork().send(printer.terminal.key);
        return { success: true };
    }
    /**
     * Send to printer
     *
     * @param printer
     * @param html
     * @param text
     * @param qrCode
     * @param options
     */
    async sendToPrinter(printer, data, localOnly = false) {
        // The receipt logo never travels to another terminal and prints only where the driver can
        // print it (receiptLogo.ts). A job without one passes through as the same object.
        const { data: job, logo } = withoutReceiptLogo(data);
        const hasLogo = logo !== null || !!data.receiptLogo;
        if (printer.printer.name === 'fp1') {
            const result = await FiscalServiceLegacy.printText(job.text);
            if (result.error) {
                return { success: false, error: 'receipt_printing_failed' };
            }
            return { success: true };
        }
        if (!printer.terminal?.key)
            return { success: false, error: 'receipt_printing_failed' };
        if (!job.codepage) {
            job.codepage = Store.get('tenant')?.venue?.settings?.cyrilic_receipt ? 'windows1251' : 'windows1250';
        }
        job.text = this.sanitizeText(job.text);
        // If connection is local, use the local print service
        const isLocal = this.terminal?.key === printer.terminal?.key;
        if (localOnly || isLocal) {
            return this.service.sendToPrinter(printer, hasLogo ? await this.restoreReceiptLogo(job, logo) : job);
        }
        // LAN-first (.preferLocalNetwork): the print tries LAN and escalates to WAN only
        // if the target doesn't reply within PRINT_LAN_WINDOW (or LAN has no peers). So a
        // same-LAN print stays on LAN with no WAN fan-out, while a printer owned by a
        // terminal on a different network is still reached over WAN. Receiver-side hash
        // dedup prints it once; the recipient filter in AppEvent.handle() ensures only the
        // target terminal acts on it.
        //   • Reached over LAN → we wait for the real reply (success/failure of delivery to
        //     the owning terminal, which then owns the hardware outcome).
        //   • Escalated to WAN → fire-and-forget: send() resolves success immediately so we
        //     never block an operation like issuing a receipt on a slow cross-network round-
        //     trip. Delivery is guaranteed by the 60s AppEvent backup poll, which re-delivers
        //     the print if the WAN copy was lost.
        const sendEventResponse = await AppEvent.build('print', { printer, data: hasLogo ? { ...job, receiptLogo: true } : job })
            .withResponse()
            .preferLocalNetwork(PRINT_LAN_WINDOW)
            .send(printer.terminal?.key, REMOTE_PRINT_TIMEOUT);
        if (!sendEventResponse[0].success) {
            return sendEventResponse[0];
        }
        return { success: true };
    }
    /**
     * Give a job its receipt logo back for this device: the logo it carried, or — for a job another
     * terminal sent without it — this terminal's own copy, if its own venue data has Logo na računu
     * on. Left out when the driver cannot print it (an older native build would print the line as
     * text).
     */
    async restoreReceiptLogo(job, logo) {
        const { receiptLogo, ...bare } = job;
        const image = logo ?? ownReceiptLogo();
        if (!image || !(await this.canPrintLogo()))
            return bare;
        return withReceiptLogo(bare, image);
    }
    /**
     * Whether this device's driver prints the receipt logo; asked once per platform service.
     */
    canPrintLogo() {
        if (!this.logoCapability) {
            this.logoCapability = this.service.canPrintLogo().then(can => can === true, () => false);
        }
        return this.logoCapability;
    }
    /**
     * Print HTML element
     */
    printHtml(element) {
        printJS({
            printable: element,
            type: 'html',
            css: '/print/print.css',
            scanStyles: false,
        });
    }
    /**
     * Place rendered HTML into the hidden #print-container both apps host
     * (the surface print-js reads from).
     */
    fillPrintContainer(html) {
        const printContainer = document.querySelector('#print-container');
        if (printContainer) {
            printContainer.innerHTML = html;
        }
    }
    /**
     * Get the printer assigned to a specific role (eg. receipt, order, food)
     */
    getAssignedPrinter(role) {
        if (!this.printConfig || this.device?.platform === 'web') {
            return;
        }
        return this.printConfig[role];
    }
    shouldGenerateHtml(printable, printer) {
        const isMobile = this.device?.platform === 'android' || this.device?.platform === 'ios';
        const isLocal = this.terminal?.key === printer?.terminal?.key;
        if (isMobile && isLocal && printable.getRenderSettings().component) {
            return false;
        }
        return true;
    }
    sanitizeText(text) {
        return text.replace(/č/g, 'c')
            .replace(/Č/g, 'C')
            .replace(/ć/g, 'c')
            .replace(/Ć/g, 'C')
            .replace(/ž/g, 'z')
            .replace(/Ž/g, 'Z')
            .replace(/đ/g, 'dj')
            .replace(/Đ/g, 'Dj')
            .replace(/š/g, 's')
            .replace(/Š/g, 'S');
    }
}
export const printService = new PrintService();
export default printService;
//# sourceMappingURL=PrintService.js.map